<template>
  <div class="header" :class="headerColor">
    <div class="container">
      <GoogleTagManager v-if="isCustomerFlow" />
      <div class="row align-items-center justify-content-between">
        <div class="col">
          <LogoM3
            @click="handleClick"
            :redirection="autoRedirection || isEcommerceFlow"
            :routeName="logoLinkRoute"
            :dark-theme="darkThemeLogo"
          />
          <div
            v-if="showSuspiciousActivityButton"
            class="ssaa"
            @click="suspiciousActivityHandler"
          >
            <span
              class="dot"
              :class="{ 'green-dot': hasSuspiciousActivity }"
            ></span>
          </div>
        </div>
        <div
          class="col text-end"
          v-if="showZendeskHelpPage && !isEcommerceFlow"
        >
          <p @click="showZendeskModal">{{ t("HELP") }}</p>
        </div>
        <div v-if="zendeskModal" class="d-flex justify-content-end">
          <ZendeskHelpModal v-if="zendeskModal" @close="closeZendeskModal" />
        </div>
        <div class="col" v-if="isEcommerceFlow && !showZendeskHelpPage">
          <div class="d-flex justify-content-end" @click="showEcommerceModal">
            <img
              class="d-inline-block"
              :src="`${useAsset(
                'img/merchant-console/question_mark_in_circle.svg'
              )}`"
              alt="help icon"
            />
            <p class="d-inline-block ms-2 pt-3">
              {{ t("HELP") }}
            </p>
          </div>
        </div>
        <div class="d-flex justify-content-end" v-if="ecommerceModal">
          <EcommerceHelpModal @close="closeEcommerceModal" />
        </div>
        <div class="col d-flex text-end justify-content-end align-items-center">
          <InConsoleSidebarSwitch />
          <div class="d-inline-block" v-if="toggleOption">
            <LanguageDropdown />
          </div>
        </div>
      </div>
      <CartTag class="p-0" />
    </div>

    <ConfirmEndOfSaleSelfServiceModal
      v-if="showConfirmEndSaleSelfServiceModal"
      :show="showConfirmEndSaleModal"
      @closeModal="closeModal"
    />

    <ConfirmEndOfSaleModal
      v-else
      :show="showConfirmEndSaleModal"
      @closeModal="closeModal"
    />

    <div v-if="isConsoleLoaded">
      <ChatBotM3 />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, watchEffect } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import router from "@/router";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { RoutesPageSteps } from "@/apps/merchant3/router/RoutesPageSteps";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";
import {
  ICartGeolocationCheckData,
  IGeolocationAction,
  GeolocationActions,
} from "@/apps/merchant3/types/CartGeolocation";
import useClerkPersistState from "@/apps/merchant3/hooks/useClerkPersistState";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import useHeartBeatCart from "@/apps/merchant3/hooks/useHeartBeatCart";
import { heartBeatPages } from "@/apps/merchant3/types/HeartBeatPages";
import ChatBotM3 from "@/apps/merchant3/components/chatbot/ChatBotM3.vue";
import ConfirmEndOfSaleModal from "@/apps/merchant3/components/ConfirmEndOfSaleModal.vue";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { checkGeolocation } from "@/apps/merchant3/services/geolocation.service";
import LanguageDropdown from "@/apps/merchant3/shared-components/ui/LanguageDropdown.vue";
import LogoM3 from "@/apps/merchant3/components/LogoM3.vue";
import ConfirmEndOfSaleSelfServiceModal from "@/apps/merchant3/components/ConfirmEndOfSaleSelfServiceModal.vue";
import { isSelfServiceSale } from "@/apps/merchant3/helpers/self-service";
import useFeatureFlag from "@/apps/merchant3/hooks/useFeatureFlag";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import ZendeskHelpModal from "@/apps/merchant3/components/help/ZendeskHelpModal.vue";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import {
  isDirectToCustomerFlow,
  isEcommerceFlow,
} from "@/apps/merchant3/helpers/checkoutFlows";
import CartTag from "@/apps/merchant3/components/CartTag.vue";
import { upadateSuspiciousActivity } from "@/apps/merchant3/services/suspiciousActivity.service";
import EcommerceHelpModal from "@/apps/merchant3/components/help/EcommerceHelpModal.vue";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import InConsoleSidebarSwitch from "@/apps/merchant3/components/in-console/ui/InConsoleSidebarSwitch.vue";
import GoogleTagManager from "@/apps/merchant3/shared-components/common/GoogleTagManager.vue";

const props = defineProps({
  logoLinkRoute: String,
  autoRedirection: {
    default: true,
    type: Boolean,
  },
  endSaleOnClick: {
    default: true,
    type: Boolean,
  },
  showChat: {
    default: true,
    type: Boolean,
  },
  showSuspiciousActivityButton: {
    default: false,
    type: Boolean,
  },
  darkThemeLogo: {
    default: false,
    type: Boolean,
  },
  headerColorClass: {
    default: "light-header",
    type: String,
  },
});

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const clerkLoaded = ref(false);
const ecommerceModal: Ref<boolean> = ref(false);
const merchantLoaded = ref(false);
const showConfirmEndSaleModal = ref(false);
const toggleOption = ref(true);
const zendeskModal = ref(false);

const hasSuspiciousActivity = ref(false);

const headerColor = computed(() => {
  return props.headerColorClass;
});

const closeEcommerceModal = () => {
  ecommerceModal.value = false;
};

const closeZendeskModal = () => {
  zendeskModal.value = false;
};

const showEcommerceModal = () => {
  logEvent(EventLogs.Events.ECOMMERCE_HELP_PAGE_CLICKED, {
    cartStep: step.value,
  });
  ecommerceModal.value = true;
};

const showZendeskModal = () => {
  logEvent(EventLogs.Events.ZENDESK_HELP_PAGE_CLICKED);
  zendeskModal.value = true;
};

const isZendeskHelpPageFlagSet = computed(() => {
  const { isFeatureFlagSet } = useFeatureFlag();
  return isFeatureFlagSet(AvailableFeatureFlags.USE_ZENDESK_HELP_PAGE);
});

const clerkCanUseZendeskHelpPage = computed(() => {
  return store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.USE_ZENDESK_HELP_PAGE
  );
});
const loadHelpSection = computed(() => {
  return (
    store.state.carts?.step !== RoutesPageSteps.MerchantLogin &&
    store.state.carts?.step !== RoutesPageSteps.ClerkLogin
  );
});

const showZendeskHelpPage = computed(() => {
  return (
    clerkCanUseZendeskHelpPage.value &&
    isZendeskHelpPageFlagSet.value &&
    loadHelpSection.value
  );
});

const geoSuccess = (position) => {
  store.dispatch("merchant/setGeolocationAllowed", true);

  const data: ICartGeolocationCheckData = {
    success: true,
    coords: {
      accuracy: position.coords?.accuracy,
      altitude: position.coords?.altitude,
      altitudeAccuracy: position.coords?.altitudeAccuracy,
      heading: position.coords?.heading,
      lat: position.coords?.latitude,
      lng: position.coords?.longitude,
      speed: position.coords?.speed,
    },
  };

  store.dispatch("merchant/setGeolocationCheckInProgress", true);

  checkGeolocation(data)
    .then((response) => {
      handleGeolocationActions(response.data.actions);
    })
    .catch((error) => {
      //TODO: Doing console log is part of this Story.
      console.error(error);
    })
    .finally(() => {
      store.dispatch("merchant/setGeolocationCheckInProgress", false);
      store.dispatch("merchant/setGeolocationCheckDone", true);
    });
};

const geoError = (position) => {
  store.dispatch("merchant/setGeolocationAllowed", false);

  const data: ICartGeolocationCheckData = {
    success: false,
    error: {
      code: position.code,
      message: position.message,
    },
  };

  store.dispatch("merchant/setGeolocationCheckInProgress", true);

  checkGeolocation(data)
    .then((response) => {
      handleGeolocationActions(response.data.actions);
    })
    .catch((error) => {
      if (
        store.state.merchant.merchantFlags.includes(
          MerchantFlags.USE_GEOFENCE
        ) &&
        store.state.merchant.merchantFlags.includes(
          MerchantFlags.USE_GEO_BLOCK
        ) &&
        store.state.merchant.merchantFlags.includes(
          MerchantFlags.USE_GEO_BLOCK_SALES_OUTSIDE_STORE
        )
      ) {
        router.push({ name: RouteName.M3Welcome, params: { countryCode: getCountryCode() } });
      }
      //TODO: Doing console log is part of this Story.
      console.log(error.response.data);
    })
    .finally(() => {
      store.dispatch("merchant/setGeolocationCheckInProgress", false);
      store.dispatch("merchant/setGeolocationCheckDone", true);
    });
};

const handleGeolocationActions = (actions: IGeolocationAction[]) => {
  store.dispatch("merchant/setGeolocationActions", actions);

  if (
    actions[GeolocationActions.SHOW_MERCHANT_SHARING_CREDENTIALS_NOTICE] ??
    false
  ) {
    const notice = {
      key: "NOTICE_MERCHANT_SHARING_CREDENTIALS",
      type: "warning",
      message: t("NOTICE_MERCHANT_SHARING_CREDENTIALS", {
        merchantName: store.state.merchant.merchant.merchantName,
      }),
    };

    store.dispatch("merchant/addNotice", notice);
  }
};

const isConsoleLoaded = computed(() => {
  return loadChat.value && merchantLoaded && clerkLoaded;
});

const step = computed(() => {
  return store.state.carts?.step;
});

const closeModal = () => {
  showConfirmEndSaleModal.value = false;
};

const loadChat = computed(() => {
  return (
    props.showChat &&
    store.state.carts?.step !== RoutesPageSteps.MerchantLogin &&
    store.state.carts?.step !== RoutesPageSteps.ClerkLogin
  );
});

const handleClick = () => {
  if (
    props.endSaleOnClick &&
    step.value !== RoutesPageSteps.Receipt &&
    step.value !== RoutesPageSteps.Welcome &&
    step.value !== RoutesPageSteps.MerchantLogin &&
    step.value !== RoutesPageSteps.ClerkLogin &&
    !isEcommerceFlow.value &&
    !isDirectToCustomerFlow.value
  ) {
    showConfirmEndSaleModal.value = true;
  }
};

toggleOption.value = route.matched.some(
  ({ name }) =>
    name === "ManagedUserLogin" ||
    name === RouteName.M3RMerchantRequestResetPassword ||
    name === RouteName.M3RMerchantRecreatePassword ||
    name === RouteName.M3ClerkLogin ||
    name === RouteName.M3MerchantLogin ||
    name === "Home"
);

onMounted(() => {
  if (
    store.state.carts?.step !== RoutesPageSteps.MerchantLogin &&
    store.state.carts?.step !== RoutesPageSteps.ClerkLogin
  ) {
    useClerkPersistState(() => (clerkLoaded.value = true));
  }

  hasSuspiciousActivity.value = store.state.carts?.ssaa;

  if (store.state.carts?.step !== RoutesPageSteps.MerchantLogin) {
    useMerchantPersistState(() => (merchantLoaded.value = true));
  }

  const currentPage = router.currentRoute.value.name?.toString();
  const isHeartBeatPage = heartBeatPages.find((page) => page === currentPage);

  if (isHeartBeatPage) {
    useHeartBeatCart();
  }
});

watchEffect(() => {
  if (
    clerkLoaded.value &&
    merchantLoaded.value &&
    store.getters["merchant/usesGeolocation"]
  ) {
    navigator.geolocation.getCurrentPosition(geoSuccess, geoError);
  }
});

const showConfirmEndSaleSelfServiceModal = isSelfServiceSale();

const suspiciousActivityHandler = () => {
  hasSuspiciousActivity.value = !hasSuspiciousActivity.value;
  try {
    const cartTag = store.state.carts.cartTag;
    upadateSuspiciousActivity(cartTag);
    logEvent(EventLogs.Events.SUSPICIOUS_ACTIVITY, {
      SuspiciousActivity: hasSuspiciousActivity.value,
    });
    logAction(ActionLogs.Events.SUSPICIOUS_ACTIVITY, {
      SuspiciousActivity: hasSuspiciousActivity.value,
    });
  } catch (error) {
    logEvent(EventLogs.Events.SUSPICIOUS_ACTIVITY_POST_FAIL, {
      SuspiciousActivity: hasSuspiciousActivity.value,
    });
    logAction(ActionLogs.Events.SUSPICIOUS_ACTIVITY_POST_FAIL, {
      SuspiciousActivity: hasSuspiciousActivity.value,
    });
  }
  return;
};

const isCustomerFlow = computed(
  () => isEcommerceFlow.value || isDirectToCustomerFlow.value
);
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
p {
  color: $pj-color-green;
  font-size: 14px;
  font-weight: bold;
  vertical-align: middle;
  cursor: pointer;
}

.header {
  @media screen and (max-width: $mobile) {
    background: none;
  }
}

.light-header {
  background: $pj-color-white;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.05);
}

.transparent-header {
  background-color: transparent;
}

.chat-icon {
  cursor: pointer;

  img {
    width: 35px;
  }
}

.d-flex {
  display: flex;
}
.justify-content-end {
  justify-content: flex-end;
}
div.ssaa {
  display: inline;
  float: right;
  cursor: pointer;
}
span.dot {
  height: 10px;
  width: 10px;
  background-color: $pj-color-light-gray;
  border-radius: 50%;
  display: inline-block;
  margin: 0 10px 0 10px;
}
span.green-dot {
  background-color: $pj-color-green;
}
:deep(.cart-tag) {
  background-color: transparent;
  padding-left: 0 !important;
}
</style>
